/**
 * One Meta/TikTok catalog product per row. The left of the row is the exact media
 * preview the Google/ADSP asset cards use (EntityAssetCard's AssetMedia at the card
 * width): image products show the image; video products show their thumbnail (Meta
 * image_link / TikTok image_url) as the poster with the Video indicator and play the
 * video (Meta video[0].url / TikTok video_url) in the native player. Beside it are the
 * product's title, product id, availability and sync status, ref tag, destination, last
 * error on a failed sync, and a Details toggle with the full payload sent to the publisher.
 */
import React, { useState } from 'react';
import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Link from '@amzn/meridian/link';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import { useTranslation } from 'src/arb-wrapper';
import type { PublisherEntityDto } from 'src/store/generated/leela-api-generated';
import {
  type CatalogProduct,
  isSafeHttpUrl,
  normalizeCatalogProduct,
  withPublishedAvailability,
} from './catalogProductHelpers';
import { AssetMedia, ENTITY_ASSET_CARD_WIDTH, type EntityAssetMedia } from './EntityAssetCard';
import { EntityMetadataDetails } from './EntityMetadataDetails';
import { EMPTY_VALUE, StatusBadge } from './StatusBadge';

/** Video products preview their thumbnail and play the video; image products show the image. */
export function toAssetMedia (product: CatalogProduct): EntityAssetMedia {
  if (product.isVideo) {
    return { assetType: 'VIDEO', assetUrl: product.videoUrl, posterUrl: product.imageUrl };
  }
  if (product.imageUrl !== undefined) {
    return { assetType: 'IMAGE', assetUrl: product.imageUrl };
  }
  // No media: an explicit empty source keeps the preview on its placeholder.
  return {};
}

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ children, label }) => (
  <Row alignmentVertical="top" spacing="200" widths={['120px', 'fill']}>
    <Text color="secondary" type="b200">{label}</Text>
    {typeof children === 'string' ? <Text type="b200">{children}</Text> : children}
  </Row>
);

interface CatalogProductRowProps {
  entity: PublisherEntityDto;
}

export const CatalogProductRow: React.FC<CatalogProductRowProps> = ({ entity }) => {
  const { t } = useTranslation();
  const [showDetails, setShowDetails] = useState(false);
  const product = normalizeCatalogProduct(entity);
  const testId = `catalog-product-${entity._id}`;
  const showError = entity.syncStatus === 'FAILED' && entity.lastError != null;
  // One status, like Google/ADSP: in stock is Live, out of stock is Removed — following what was published.
  const badgeEntity = withPublishedAvailability(entity);

  return (
    <Box data-testid={testId} overflowX="hidden" overflowY="hidden" type="outline">
      <Row alignmentVertical="top" spacing="none" widths={[`${ENTITY_ASSET_CARD_WIDTH}px`, 'fill']}>
        <AssetMedia entityId={entity._id} metadata={toAssetMedia(product)} />
        <Box spacingInset="300">
          <Column spacing="200">
            <Row alignmentHorizontal="justify" alignmentVertical="top" spacing="200">
              <Text type="b300">{product.title ?? t('ads_manager_catalog_untitled_product')}</Text>
              <span data-testid={`${testId}-status`}><StatusBadge entity={badgeEntity} /></span>
            </Row>

            <Field label={t('ads_manager_catalog_product_id')}>{product.productId ?? entity.sourceEntityId ?? EMPTY_VALUE}</Field>
            <Field label={t('ads_manager_catalog_ref_tag')}>{product.refTag ?? EMPTY_VALUE}</Field>
            {product.gti != null && <Field label={t('ads_manager_col_gti')}>{product.gti}</Field>}
            <Field label={t('ads_manager_catalog_destination')}>
              {isSafeHttpUrl(product.link)
                ? (
                  <Link data-testid={`${testId}-link`} href={product.link} rel="noopener noreferrer" target="_blank">
                    <span style={{ wordBreak: 'break-all' }}>{product.link}</span>
                  </Link>
                  )
                : (product.link ?? EMPTY_VALUE)}
            </Field>
            {showError && (
              <Field label={t('ads_manager_col_last_error')}>
                <Text color="error" data-testid={`${testId}-error`} type="b200">{entity.lastError}</Text>
              </Field>
            )}

            {/* A real button (keyboard-focusable), styled like EntityAssetCard's Details toggle. */}
            <button
              aria-expanded={showDetails}
              data-testid={`${testId}-show-details`}
              style={{ background: 'none', border: 'none', color: '#0972d3', cursor: 'pointer', fontSize: '12px', padding: 0, textAlign: 'left' }}
              type="button"
              onClick={() => { setShowDetails(prev => !prev); }}
            >
              {t('ads_manager_details')}
            </button>
            {showDetails && (
              <Column data-testid={`${testId}-details`} spacing="200">
                <Text type="b200">{t('ads_manager_catalog_payload_header')}</Text>
                <EntityMetadataDetails layout="row" metadata={product.payload} testId={`${testId}-payload`} />
              </Column>
            )}
          </Column>
        </Box>
      </Row>
    </Box>
  );
};
